import { forwardRef } from "react";
import { type Control, type FieldPath, type FieldValues } from "react-hook-form";
import { AsyncSelectField } from "@/components/form";
import { AsyncSearchableSelect, type SearchableOption } from "@/components/SearchableSelect";
import { users, type UserListItem } from "@/features/users/api/users.api";

const USER_PAGE_SIZE = 20;

function hintOf(email: string | null, userNumber: number | null) {
  return [email || null, userNumber != null ? `#${userNumber}` : null].filter(Boolean).join(" · ") || undefined;
}

export function userOption(u: Pick<UserListItem, "id" | "name" | "email" | "userNumber">): SearchableOption {
  return { value: u.id, label: u.name || u.email || "(unnamed user)", hint: hintOf(u.email, u.userNumber) };
}

export async function searchUserOptions({ keyword, pageNumber }: { keyword: string; pageNumber: number }) {
  const page = await users.list({
    keyword,
    pageNumber,
    pageSize: USER_PAGE_SIZE,
    sort: [{ by: "name", dir: "asc" }],
  });
  return {
    options: page.items.map(userOption),
    hasNextPage: page.hasNextPage,
    totalCount: page.totalCount,
  };
}

export async function resolveUserOption(id: string): Promise<SearchableOption | null> {
  const u = await users.get(id);
  return u?.id ? userOption(u) : null;
}

export const UserSelect = forwardRef<HTMLButtonElement, {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  pinnedOptions?: SearchableOption[];
  exclude?: string[];
  disabled?: boolean;
  className?: string;
  id?: string;
  onError?: (error: unknown) => void;
}>(function UserSelect({
  value, onValueChange, placeholder = "Select a user…", pinnedOptions, exclude, disabled, className, id, onError,
}, ref) {
  return (
    <AsyncSearchableSelect
      ref={ref}
      value={value}
      onValueChange={onValueChange}
      search={searchUserOptions}
      resolveValue={resolveUserOption}
      pinnedOptions={pinnedOptions}
      exclude={exclude}
      placeholder={placeholder}
      searchPlaceholder="Search users…"
      emptyText="No matching users."
      disabled={disabled}
      className={className}
      id={id}
      onError={onError}
    />
  );
});

export function UserSelectField<T extends FieldValues>({
  control, name, label, required, placeholder, hint, className, disabled, pinnedOptions, exclude, onValueMissing, onError,
}: {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  required?: boolean;
  placeholder?: string;
  hint?: string;
  className?: string;
  disabled?: boolean;
  pinnedOptions?: SearchableOption[];
  exclude?: string[];
  onValueMissing?: () => void;
  onError?: (error: unknown) => void;
}) {
  return (
    <AsyncSelectField
      control={control}
      name={name}
      label={label}
      required={required}
      placeholder={placeholder ?? "Select a user…"}
      hint={hint}
      className={className}
      disabled={disabled}
      search={searchUserOptions}
      resolveValue={resolveUserOption}
      pinnedOptions={pinnedOptions}
      exclude={exclude}
      onValueMissing={onValueMissing}
      onError={onError}
    />
  );
}
